import { Overlay, Spinner, SpinnerSize } from '@fluentui/react';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { SloDefinitions, TelemetryEventType, logService, telemetryService } from 'npo-common';
import * as React from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { ORG_TYPE_NONPROFIT } from 'src/common/Constants';
import FollowUsFooter from 'src/components/followUsFooter/FollowUsFooter';
import { ContactUsSection } from 'src/components/offers/shared/ContactUsSection';
import { StatusIcon } from 'src/components/offers/shared/StatusBanner';
import AddressVerificationModal, { IAddress } from 'src/components/profilev2/AddressVerificationModal';
import BannerNotification from 'src/components/profilev2/BannerNotification';
import ConfirmationModal from 'src/components/profilev2/ConfirmationModal';
import DiscountAndDonationPolicies from 'src/components/profilev2/DiscountAndDonationPolicies';
import MultiDocUpload from 'src/components/profilev2/MultiDocUpload';
import OrgInfoSection2Edit from 'src/components/profilev2/OrgInfoSection2Edit';
import OrgInfoSection2View from 'src/components/profilev2/OrgInfoSection2View';
import OrgRegistrationInfo from 'src/components/profilev2/OrgRegistrationInfo';
import PrimaryContactEdit from 'src/components/profilev2/PrimaryContactEdit';
import PrimaryContactView from 'src/components/profilev2/PrimaryContactView';
import { ProfileCollapse } from 'src/components/profilev2/ProfileCollapse';
import RegistrationIdentifiersEdit from 'src/components/profilev2/RegistrationIdentifiersEdit';
import RegistrationIdentifiersView from 'src/components/profilev2/RegistrationIdentifiersView';
import StatusHeader from 'src/components/profilev2/StatusHeader';
import Text from 'src/components/text/Text';
import EmailVerification from 'src/components/verification/EmailVerification';
import { isEmailVerificationRequired, isNonprofitValidationRequired } from 'src/components/verification/Utils';
import tradescreenService, {
  IAddressValidationResponseAddress,
  ValidationMode
} from 'src/dataServices/EligibilityService';
import localeService from 'src/dataServices/LocaleService';
import meoLocaleService from 'src/dataServices/MeoLocaleService';
import {
  isAddressAllowedByResponseRules,
  shouldSkipAvsValidation,
  shouldBlockSubmissionOnAvsException,
  shouldBlockSubmissionOnNon200AvsResponse
} from './avsResponseRules';
import { IApplicationState } from 'src/state';
import { IFeatureFlagsState } from 'src/state/feature-flags/types';
import { MeoLocaleState } from 'src/state/meo-localization/types';
import { IRegistrationIdentifier, NonprofitStatus } from 'src/state/nonprofit/types';
import {
  createProfile,
  saveProfile,
  setEditMode,
  setShowAddressPopup,
  setShowConfirmation
} from 'src/state/profile/actions';
import { IProfile, IProfileDocument, IProfileState } from 'src/state/profile/types';
import './ProfilePageV2.scss';

interface IPropsFromState {
  editMode: boolean;
  showConfirmation: boolean;
  showAddressPopup: boolean;
  isCommercial: boolean;
  profileState: IProfileState;
  profileError: string | undefined;
  emailVerificationRequired: boolean;
  meoLocale: MeoLocaleState;
  featureFlags: IFeatureFlagsState;
}

interface IPropsFromDispatch {
  setShowConfirmation: Function;
  setShowAddressPopup: Function;
  setEditMode: Function;
  setCommercial: Function;
  saveProfile: typeof saveProfile;
  createProfile: typeof createProfile;
}

type IProfilePropsV2 = IPropsFromState & IPropsFromDispatch;

export const ProfilePageV2 = ({
  editMode,
  setEditMode,
  showConfirmation,
  setShowConfirmation,
  showAddressPopup,
  setShowAddressPopup,
  isCommercial,
  profileState,
  saveProfile,
  createProfile,
  profileError,
  emailVerificationRequired,
  meoLocale,
  featureFlags
}: IProfilePropsV2) => {
  const initialEmailLocale = isCommercial
    ? meoLocaleService.resolveLocale(localeService.getlocale(), meoLocale.locales)
    : meoLocaleService.resolveLocale(profileState.profile.primaryContact.emailLanguagePreference, meoLocale.locales);

  const getProfileInitialValues = () => {
    if (profileState.profile.notificationEmails && profileState.profile.notificationEmails !== null) {
      profileState.profile.notificationEmails = profileState.profile.notificationEmails.filter(
        (email) => email !== profileState.profile.primaryContact.email
      );
    }

    return profileState.profile;
  };

  const [profileInitialValues, setProfileInitialValues] = React.useState(getProfileInitialValues());
  const [suggestedAddresses, setSuggestedAddresses] = React.useState<IAddress[]>([]);
  const [isValidatingAddress, setIsValidatingAddress] = React.useState(false);
  const [AVSResponseCode, setAVSResponseCode] = React.useState<number>(200);
  const [showInvalidAddressError, setShowInvalidAddressError] = React.useState(false);
  const [isEditingRegistration, setIsEditingRegistration] = React.useState<boolean>(false);
  const [showDocumentRequiredError, setShowDocumentRequiredError] = React.useState(false);
  const regEditv2Enabled = featureFlags.regEditv2Enabled;
  const multiRegIdSelectionEnabled = featureFlags.enableMultiRegIdSelection;
  const isProfileIncomplete = profileState.profile?.isIncomplete ?? false;
  // Profile update / attestation behavior is gated by the ProfileAttestationEnabled feature flag.
  const isProfileUpdateRequired =
    (featureFlags.ProfileAttestationEnabled && (profileState.profile?.isProfileUpdateRequired ?? false)) || false;

  // Force edit mode when API flagged the profile as requiring an update -
  // the user must update before being allowed out.
  React.useEffect(() => {
    if (isProfileUpdateRequired && !editMode) {
      setEditMode(true);
    }
  }, [isProfileUpdateRequired, editMode, setEditMode]);

  // Log once when isProfileUpdateRequired is first detected as true.
  const hasLoggedProfileUpdateRequired = React.useRef(false);
  React.useEffect(() => {
    if (isProfileUpdateRequired && !hasLoggedProfileUpdateRequired.current) {
      hasLoggedProfileUpdateRequired.current = true;
      try {
        logService.logEvent({
          Name: 'ProfileUpdateRequiredDetected',
          EventType: TelemetryEventType.PrimaryWorkflow,
          SloId: SloDefinitions.NonprofitProfileUpdate,
          Properties: {
            nonprofitId: profileState.profile?.id ?? '',
            nonprofitStatus: profileState.profile?.status ?? '',
            profileLastUpdatedDate: profileState.profile?.profileLastUpdatedDate?.toISOString() ?? ''
          }
        });
      } catch {
        // telemetry must never break the UI
      }
    }
  }, [isProfileUpdateRequired, profileState.profile]);

  const onFieldChange = (field: string, value: string, change: any, preserveOrganizationType?: boolean) => {
    if (field === 'organizationType') {
      setShowDocumentRequiredError(false);
      if (featureFlags.profilePageEnhancementsEnabled) {
        const initialOrgTypeEmpty = !profileInitialValues.organizationType;
        const switchingToNonprofitWithNoInitialOrgType = initialOrgTypeEmpty && value === ORG_TYPE_NONPROFIT;

        // If switching back to the original organization type, restore the original identifiers
        // Otherwise, clear legal identifiers unless organizationType was empty and switching to nonprofit
        if (value === profileInitialValues.organizationType) {
          change('registrationIdentifiers', profileInitialValues.registrationIdentifiers);
        } else if (!switchingToNonprofitWithNoInitialOrgType) {
          change('registrationIdentifiers', []);
        }

        // Automatically open legal identifiers in edit mode, unless org type was null and switching to nonprofit
        if (!switchingToNonprofitWithNoInitialOrgType) {
          setIsEditingRegistration(true);
        }
      }
    }

    if (field === 'address.countryCode') {
      change('address.addressLine1', '');
      change('address.addressOther', '');
      change('address.city', '');
      change('address.stateRegion', '');
      change('address.postalCode', '');
      change('address.phone', '');
      // Only clear organizationType if the feature flag is enabled; otherwise preserve it
      if (!preserveOrganizationType) {
        change('organizationType', '');
      }
      change('registrationIdentifiers', []);
      // Open the editor so the cleared identifiers are re-entered and validated before submit
      setIsEditingRegistration(true);
    }
  };

  const onEditClicked = () => {
    setProfileInitialValues(profileState.profile);
    setShowDocumentRequiredError(false);
    setShowInvalidAddressError(false);
    setEditMode(true);
    // Auto-open registration identifiers if profile is not in progress and identifiers are empty
    if (isProfileIncomplete) {
      setIsEditingRegistration(true);
    }
  };

  // Auto-open legal identifiers in edit mode if profile is NOT in progress and has no identifiers
  React.useEffect(() => {
    if (editMode && isProfileIncomplete) {
      setIsEditingRegistration(true);
    } else if (!editMode) {
      setIsEditingRegistration(false);
    }
  }, [editMode, isProfileIncomplete]);

  const onSubmitClicked = async (form: FormApi<IProfile>) => {
    const formState = form.getState();

    // Trigger the browser's native constraint validation so any invalid field(e.g. an empty required field) is highlighted
    const formElement = document.getElementById('profile-v2') as HTMLFormElement | null;
    if (formElement && typeof formElement.reportValidity === 'function' && !formElement.reportValidity()) {
      return;
    }

    // Run react-final-form field-level validation (custom validators). When the form is invalid, calling form.submit() marks all fields touched so their error messages render, and does not invoke onSubmit while invalid.
    if (formState.invalid) {
      form.submit();
      return;
    }

    // Identifiers can be cleared while the editor is collapsed, where no field-level validator is
    // mounted to catch it. Re-open the editor rather than saving an empty array.
    if (!hasRegistrationIdentifiers(formState.values)) {
      setIsEditingRegistration(true);
      return;
    }

    if (isDocumentUploadRequiredForLegalIdentifierChanges(formState.initialValues, formState.values)) {
      setShowDocumentRequiredError(true);
      return;
    }

    setShowDocumentRequiredError(false);
    setShowInvalidAddressError(false);

    if (featureFlags.AVSEnabled) {
      // check if address fields have changed
      const dirtyFields = form.getState().dirtyFields;
      const addressFieldsDirty = Object.keys(dirtyFields).some(
        (field) =>
          field === 'address.addressLine1' ||
          field === 'address.addressOther' ||
          field === 'address.city' ||
          field === 'address.postalCode' ||
          field === 'address.stateRegion'
      );

      const formValues = form.getState().values;
      const address = formValues.address;

      if (shouldSkipAvsValidation(addressFieldsDirty, address?.stateRegion || '')) {
        document.body.style.overflow = 'hidden';
        setShowConfirmation(true);
        return;
      }

      setIsValidatingAddress(true);

      // Call validateAddress API
      try {
        const validationRequest = {
          country: address?.countryCode || '',
          region: address?.stateRegion || '',
          city: address?.city || '',
          address_line1: address?.addressLine1 || '',
          address_line2: address?.addressOther || '',
          postal_code: address?.postalCode || '',
          validation_mode: ValidationMode.BUSINESS,
          secondary_validation_mode: ValidationMode.LEGACY_BUSINESS
        };

        const response = await tradescreenService.validateAddress(validationRequest);

        if (response.status === 200 && response.value) {
          if (featureFlags.avsResponseRulesEnabled && !isAddressAllowedByResponseRules(response.value)) {
            setAVSResponseCode(400);
            setShowInvalidAddressError(true);
            setShowAddressPopup(false);
            return;
          }

          // Map API response addresses to modal format
          const mappedAddresses: IAddress[] =
            response.value.suggested_addresses?.map((addr: IAddressValidationResponseAddress) => ({
              country: addr.country,
              region: addr.region,
              city: addr.city,
              addressLine1: addr.address_line1,
              addressLine2: addr.address_line2 || '',
              postalCode: addr.postal_code
            })) || [];

          // No suggestions, go straight to confirmation
          if (mappedAddresses.length === 0) {
            document.body.style.overflow = 'hidden';
            setShowConfirmation(true);
            setIsValidatingAddress(false);
            return;
          }

          // Single suggestion that matches entered address, go straight to confirmation
          if (
            mappedAddresses.length === 1 &&
            validationRequest.address_line1 === mappedAddresses[0].addressLine1 &&
            validationRequest.address_line2 === mappedAddresses[0].addressLine2 &&
            validationRequest.city === mappedAddresses[0].city &&
            validationRequest.region === mappedAddresses[0].region &&
            validationRequest.postal_code === mappedAddresses[0].postalCode &&
            validationRequest.country === mappedAddresses[0].country
          ) {
            document.body.style.overflow = 'hidden';
            setShowConfirmation(true);
            setIsValidatingAddress(false);
            return;
          }

          setSuggestedAddresses(mappedAddresses);
          setAVSResponseCode(response.status);
          setShowAddressPopup(true);
        } else if (response.status === 429) {
          setAVSResponseCode(response.status);
          if (shouldBlockSubmissionOnNon200AvsResponse(featureFlags.avsResponseRulesEnabled)) {
            setShowInvalidAddressError(true);
            setShowAddressPopup(false);
            return;
          }

          // For 429, show the popup with the rate limit banner
          setShowAddressPopup(true);
        } else {
          setAVSResponseCode(response.status ?? 500);
          if (shouldBlockSubmissionOnNon200AvsResponse(featureFlags.avsResponseRulesEnabled)) {
            setShowInvalidAddressError(true);
            setShowAddressPopup(false);
            return;
          }

          // For any other non-200 response, skip popup and go to confirmation
          document.body.style.overflow = 'hidden';
          setShowConfirmation(true);
          setIsValidatingAddress(false);
          return;
        }
      } catch (error) {
        setAVSResponseCode(500);
        if (shouldBlockSubmissionOnAvsException(featureFlags.avsResponseRulesEnabled)) {
          setShowInvalidAddressError(true);
          setShowAddressPopup(false);
          return;
        }

        // On error, skip the popup and go straight to confirmation
        document.body.style.overflow = 'hidden';
        setShowConfirmation(true);
        setIsValidatingAddress(false);
        return;
      } finally {
        setIsValidatingAddress(false);
      }
    } else {
      document.body.style.overflow = 'hidden';
      setShowConfirmation(true);
    }
  };

  const onAddressSubmitClicked = () => {
    document.body.style.overflow = 'hidden';
    setShowConfirmation(true);
  };

  const onCancelClicked = (form: FormApi<IProfile>) => {
    form.reset();
    setShowDocumentRequiredError(false);
    setShowInvalidAddressError(false);
    setEditMode(false);
  };

  const handleSubmit = (values: IProfile) => {
    updateProfilePayload(values, profileState.profile.documents, values.organizationType !== ORG_TYPE_NONPROFIT);

    // set default values for legacy nonprofits to stay in sync with TechSoup payload
    values.missionStatement = 'nil';
    values.operatingBudget = 'nil';
    values.organizationSize = 'nil';

    if (isCommercial) {
      createProfile(values);
    } else {
      saveProfile(values);
    }

    trackEmailLanguagePreferenceUpdates(values);

    try {
      const revalidationRequired = featureFlags
        ? isNonprofitValidationRequired(profileInitialValues, values, featureFlags)
        : false;
      const orgNameChanged = (profileInitialValues?.name ?? '') !== (values?.name ?? '');
      const orgTypeChanged = (profileInitialValues?.organizationType ?? '') !== (values?.organizationType ?? '');
      const legalIdentifiersChanged = hasRegistrationIdentifiersChanged(profileInitialValues, values);
      const documentsChanged = hasDocumentsChanged(profileInitialValues, values);
      const totalDocumentsBefore = (profileInitialValues?.documents ?? []).filter((d) => Boolean(d?.name)).length;
      const totalDocumentsAtSubmit = (values?.documents ?? []).filter((d) => Boolean(d?.name)).length;

      logService.logEvent({
        Name: 'ProfileSubmission',
        EventType: TelemetryEventType.PrimaryWorkflow,
        SloId: SloDefinitions.NonprofitProfileUpdate,
        Properties: {
          nonprofitId: profileState.profile?.id ?? '',
          nonprofitStatus: profileState.profile?.status ?? '',
          initialOrganizationType: profileInitialValues?.organizationType ?? '',
          organizationType: values?.organizationType ?? '',
          countryCode: values?.address?.countryCode ?? '',
          revalidationRequired: String(revalidationRequired),
          orgNameChanged: String(orgNameChanged),
          orgTypeChanged: String(orgTypeChanged),
          legalIdentifiersChanged: String(legalIdentifiersChanged),
          documentsChanged: String(documentsChanged),
          totalDocumentsBefore: String(totalDocumentsBefore),
          totalDocumentsAtSubmit: String(totalDocumentsAtSubmit)
        }
      });
    } catch {
      // telemetry must never break the UI
    }
  };

  const trackEmailLanguagePreferenceUpdates = (values: IProfile) => {
    // commerical signup
    if (isCommercial) {
      logService.logEvent({
        Name: 'InitialEmailLanguagePreference',
        EventType: TelemetryEventType.PrimaryWorkflow,
        SloId: SloDefinitions.ExistingTenantRegistration,
        Properties: {
          browserLocale: localeService.getlocale(),
          defaultLocale: initialEmailLocale.cultureCode,
          selectedLocale: values.primaryContact.emailLanguagePreference
        }
      });

      return; // return to avoid nesting
    }

    // profile update - only track if the email language preference has changed
    if (values.primaryContact.emailLanguagePreference !== initialEmailLocale.cultureCode) {
      logService.logEvent({
        Name: 'UpdateEmailLanguagePreference',
        EventType: TelemetryEventType.PrimaryWorkflow,
        SloId: SloDefinitions.NonprofitProfileUpdate,
        Properties: {
          browserLocale: localeService.getlocale(),
          existingLocale: initialEmailLocale.cultureCode,
          selectedLocale: values.primaryContact.emailLanguagePreference
        }
      });
    }
  };

  // validate first and last name fields to ensure they are not both single characters at the same time
  const validateNames = (values: any) => {
    if (featureFlags.tradescreeningPhaseTwoEnabled) {
      const errors: any = {};
      const firstName = values?.primaryContact?.firstName || '';
      const lastName = values?.primaryContact?.lastName || '';

      // Use Array.from to get actual visual character count (handles emojis, surrogate pairs, etc.)
      const firstNameCharCount = Array.from(firstName).length;
      const lastNameCharCount = Array.from(lastName).length;

      if (firstNameCharCount === 1 && lastNameCharCount === 1) {
        errors.primaryContact = {
          ...(errors.primaryContact || {}),
          firstName: 'primaryContact.firstNameError',
          lastName: 'primaryContact.lastNameError'
        };
      }

      return errors;
    }
  };

  return (
    <section id="profile-page">
      {isValidatingAddress && (
        <Overlay className="profile-validation-overlay">
          <Spinner
            size={SpinnerSize.large}
            label={localeService.getText('Shared', 'SpinnerPleaseWait')}
            ariaLive="assertive"
          />
        </Overlay>
      )}
      {emailVerificationRequired && <EmailVerification />}
      {isCommercial ? (
        <BannerNotification type={StatusIcon.info} text={localeService.getText('ProfileV2', 'ConvertAccountMode')} />
      ) : !profileState.profile.isEditable &&
        !isProfileUpdateRequired &&
        !featureFlags.profilePageEnhancementsEnabled ? (
        <BannerNotification type={StatusIcon.recent} text="">
          {new Text({ category: 'ProfileV2', id: 'NotEditableText' }).render()}
        </BannerNotification>
      ) : editMode && !isProfileUpdateRequired ? (
        <BannerNotification type={StatusIcon.info} text={localeService.getText('ProfileV2', 'EditMode')} />
      ) : (
        <></>
      )}
      {profileError ? (
        <BannerNotification
          type={StatusIcon.error}
          text={localeService.getText('ProfileV2', 'ErrorDuringProcessing')}
        />
      ) : (
        <></>
      )}
      {showInvalidAddressError ? (
        <BannerNotification
          type={StatusIcon.error}
          text={localeService.getText('ProfileV2', 'InvalidAddressErrorBannerMessage')}
        />
      ) : (
        <></>
      )}
      {isProfileUpdateRequired && profileState.profile?.status === NonprofitStatus.Approved && (
        <BannerNotification type={StatusIcon.info} text={localeService.getText('ProfileV2', 'ProfileUpdateRequired')} />
      )}

      <Form
        initialValues={profileInitialValues}
        onSubmit={handleSubmit}
        mutators={{ ...arrayMutators }}
        validate={validateNames}
        render={({ handleSubmit, form }) => {
          const selectedCountryCode =
            form.getState().values.address?.countryCode || profileState.profile.address.countryCode;
          const selectedOrganizationType =
            form.getState().values.organizationType || profileState.profile.organizationType;
          const showSupportingDocuments =
            featureFlags.profilePageEnhancementsEnabled && selectedOrganizationType === ORG_TYPE_NONPROFIT;
          const selectedDocuments = form.getState().values.documents || profileState.profile.documents;
          const showDocumentUploadError =
            showDocumentRequiredError &&
            isDocumentUploadRequiredForLegalIdentifierChanges(form.getState().initialValues, form.getState().values);
          const orgTypeChanged =
            selectedOrganizationType !==
            (form.getState().initialValues?.organizationType || profileState.profile.organizationType);

          const handleActionLegalIdentifiers = () => {
            if (isEditingRegistration) {
              form.change('registrationIdentifiers', profileState.profile.registrationIdentifiers);
              setIsEditingRegistration(false);
            } else {
              setIsEditingRegistration(true);
            }
          };

          return (
            <form
              id="profile-v2"
              className="profile-form"
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                const activeTag = document.activeElement?.tagName.toLowerCase();
                if (e.key === 'Enter' && activeTag !== 'a' && document.activeElement?.id !== 'profileFormSubmitBtnId') {
                  e.preventDefault(); // prevent implicit form submission via enter key (unless submit button is focused)
                }
              }}
            >
              <StatusHeader showNonprofitStatus={!isCommercial} />
              <ProfileCollapse
                organizationRegistrationInfo={!isCommercial ? <OrgRegistrationInfo /> : undefined}
                organizationInfo={
                  editMode ? (
                    <OrgInfoSection2Edit
                      profile={profileState.profile}
                      profileForm={form}
                      onFieldChange={(field, value, preserveOrgType) =>
                        onFieldChange(
                          field,
                          value,
                          form.change,
                          !featureFlags.profilePageEnhancementsEnabled || preserveOrgType
                        )
                      }
                    />
                  ) : (
                    <OrgInfoSection2View profile={profileState.profile} />
                  )
                }
                primaryContact={
                  editMode ? (
                    <PrimaryContactEdit
                      profile={profileState.profile}
                      WrappedFieldArrayProps={undefined}
                      isCommercial={isCommercial}
                      initialEmailLocale={initialEmailLocale}
                    />
                  ) : (
                    <PrimaryContactView profile={profileState.profile} />
                  )
                }
                legalIdentifiers={
                  editMode ? (
                    regEditv2Enabled && !isCommercial ? (
                      isEditingRegistration ? (
                        <div className="reg-edit-wrapper mt-3">
                          <RegistrationIdentifiersEdit
                            profileForm={form}
                            registrationIdentifiers={profileState.profile.registrationIdentifiers}
                            countryCode={selectedCountryCode}
                            enableMultiRegIdSelection={multiRegIdSelectionEnabled}
                            organizationType={selectedOrganizationType}
                          />
                        </div>
                      ) : (
                        <div className="reg-edit-wrapper">
                          <RegistrationIdentifiersView
                            registrationIdentifiers={profileState.profile.registrationIdentifiers}
                            countryCode={selectedCountryCode}
                            organizationType={selectedOrganizationType}
                          />
                        </div>
                      )
                    ) : (
                      <RegistrationIdentifiersEdit
                        profileForm={form}
                        registrationIdentifiers={profileState.profile.registrationIdentifiers}
                        countryCode={selectedCountryCode}
                        enableMultiRegIdSelection={multiRegIdSelectionEnabled}
                        organizationType={selectedOrganizationType}
                      />
                    )
                  ) : (
                    <RegistrationIdentifiersView
                      registrationIdentifiers={profileState.profile.registrationIdentifiers}
                      countryCode={selectedCountryCode}
                      organizationType={selectedOrganizationType}
                    />
                  )
                }
                isEditMode={editMode && regEditv2Enabled}
                OnActionButtonClick={handleActionLegalIdentifiers}
                actionButtonText={isEditingRegistration ? 'Cancel' : 'Change'}
                showActionButton={
                  !isCommercial &&
                  regEditv2Enabled &&
                  !isProfileIncomplete &&
                  !(isEditingRegistration && orgTypeChanged)
                }
                profilePageEnhancementsEnabled={featureFlags.profilePageEnhancementsEnabled}
                supportingDocuments={
                  showSupportingDocuments ? (
                    <MultiDocUpload
                      isEditMode={editMode}
                      organizationType={selectedOrganizationType}
                      documents={selectedDocuments}
                      showDocumentRequiredError={showDocumentUploadError}
                      onDocumentsChange={(documents) => {
                        setShowDocumentRequiredError(false);
                        form.change('documents' as any, documents);
                      }}
                      nonprofitId={profileState.profile?.id}
                      nonprofitStatus={profileState.profile?.status}
                    />
                  ) : undefined
                }
              />
              <div className="profile-edit-policy">
                <DiscountAndDonationPolicies />
                <FormButtons
                  editMode={editMode}
                  isEditable={profileState.profile.isEditable}
                  isCommercial={isCommercial}
                  profilePageEnhancementsEnabled={featureFlags.profilePageEnhancementsEnabled}
                  onEditClicked={onEditClicked}
                  onSubmitClicked={() => onSubmitClicked(form)}
                  onCancelClicked={() => onCancelClicked(form)}
                  form={form}
                  isValidatingAddress={isValidatingAddress}
                  isProfileUpdateRequired={isProfileUpdateRequired}
                />
              </div>
              <AddressVerificationModal
                onSubmit={() => {
                  setShowAddressPopup(false);
                  onAddressSubmitClicked();
                }}
                onCancel={() => {
                  setShowAddressPopup(false);
                }}
                onApplyAddress={(addr) => {
                  // Update form fields to reflect selected/verified address
                  // Cast needed: final-form supports dot-notation paths at runtime but types only allow keyof FormValues
                  const change = form.change as (name: string, value?: unknown) => void;
                  form.batch(() => {
                    form.change('address.countryCode' as any, addr.country);
                    form.change('address.stateRegion' as any, addr.region);
                    form.change('address.city' as any, addr.city);
                    form.change('address.addressLine1' as any, addr.addressLine1);
                    form.change('address.addressOther' as any, addr.addressLine2);
                    form.change('address.postalCode' as any, addr.postalCode);
                  });
                }}
                showAddressModal={showAddressPopup}
                originalAddress={{
                  country: form.getState().values.address?.countryCode || '',
                  region: form.getState().values.address?.stateRegion || '',
                  city: form.getState().values.address?.city || '',
                  addressLine1: form.getState().values.address?.addressLine1 || '',
                  addressLine2: form.getState().values.address?.addressOther || '',
                  postalCode: form.getState().values.address?.postalCode || ''
                }}
                suggested={suggestedAddresses}
                AVSResponseCode={AVSResponseCode}
              />
              <ConfirmationModal
                showConfirmation={showConfirmation}
                revalidationRequired={isNonprofitValidationRequired(
                  form.getState().initialValues,
                  form.getState().values,
                  featureFlags
                )}
                onConfirmation={() => {
                  document.body.style.overflow = 'auto';
                  setShowConfirmation(false);
                }}
                onCancelConfirmation={() => {
                  document.body.style.overflow = 'auto';
                  setShowConfirmation(false);
                  const focusSubmitButton = () => {
                    const submitButton = document.getElementById('profileFormSubmitBtnId');
                    if (submitButton) {
                      submitButton?.focus();
                    } else {
                      requestAnimationFrame(focusSubmitButton);
                    }
                  };
                  requestAnimationFrame(focusSubmitButton);
                }}
                isCommercial={isCommercial}
                profilePageEnhancementsEnabled={featureFlags.profilePageEnhancementsEnabled}
              />
            </form>
          );
        }}
      />
      <ContactUsSection />
      <FollowUsFooter />
    </section>
  );
};

const updateProfilePayload = (
  payload: IProfile,
  existingDocuments?: IProfileDocument[],
  preserveExistingDocuments?: boolean
) => {
  if (!payload) {
    return;
  }

  if (preserveExistingDocuments || payload.documents === undefined || payload.documents === null) {
    payload.documents = existingDocuments || [];
  }

  if (!Array.isArray(payload.documents)) {
    payload.documents = [];
  }

  if (payload.notificationEmails) {
    payload.notificationEmails = payload.notificationEmails.filter((email) => email !== undefined && email !== null);
  }

  if (
    payload.notificationEmails === undefined ||
    payload.notificationEmails === null ||
    payload.notificationEmails.length === 0
  ) {
    payload.notificationEmails = [payload.primaryContact.email];
  }

  if (!payload.activityCode) {
    payload.activityCode = 'nil';
  }
};

const getSortedRegistrationIdentifierSignatures = (registrationIdentifiers?: IRegistrationIdentifier[]): string[] => {
  return (registrationIdentifiers || [])
    .map((identifier) => {
      return [
        identifier?.identifierKey || '',
        identifier?.identifierAcronym || '',
        identifier?.regulatoryBodyAcronym || '',
        identifier?.legalIdentifier || '',
        identifier?.regulatoryBody || ''
      ].join('|');
    })
    .sort();
};

const hasRegistrationIdentifiersChanged = (
  initialValues: Partial<IProfile> | undefined,
  currentValues: Partial<IProfile> | undefined
): boolean => {
  const initialSignatures = getSortedRegistrationIdentifierSignatures(initialValues?.registrationIdentifiers);
  const currentSignatures = getSortedRegistrationIdentifierSignatures(currentValues?.registrationIdentifiers);

  return JSON.stringify(initialSignatures) !== JSON.stringify(currentSignatures);
};

const hasDocumentsChanged = (
  initialValues: Partial<IProfile> | undefined,
  currentValues: Partial<IProfile> | undefined
): boolean => {
  const initialDocuments = initialValues?.documents || [];
  const currentDocuments = currentValues?.documents || [];

  return JSON.stringify(currentDocuments) !== JSON.stringify(initialDocuments);
};

const hasRegistrationIdentifiers = (values: Partial<IProfile> | undefined): boolean =>
  (values?.registrationIdentifiers || []).some((identifier) => Boolean(identifier?.legalIdentifier));

const isDocumentUploadRequiredForLegalIdentifierChanges = (
  initialValues: Partial<IProfile> | undefined,
  currentValues: Partial<IProfile> | undefined
): boolean => {
  const selectedOrganizationType = currentValues?.organizationType || initialValues?.organizationType;

  if (selectedOrganizationType !== ORG_TYPE_NONPROFIT) {
    return false;
  }

  const legalIdentifiersChanged = hasRegistrationIdentifiersChanged(initialValues, currentValues);
  const currentDocuments = (currentValues?.documents || []).filter((document) => Boolean(document?.name));

  return legalIdentifiersChanged && currentDocuments.length === 0;
};

interface IFormButtonsPros {
  editMode: boolean;
  isEditable: boolean;
  isCommercial: boolean;
  profilePageEnhancementsEnabled: boolean;
  onEditClicked: React.MouseEventHandler<HTMLButtonElement>;
  onSubmitClicked: () => void;
  onCancelClicked: React.MouseEventHandler<HTMLButtonElement>;
  form: FormApi<IProfile>;
  isValidatingAddress: boolean;
  isProfileUpdateRequired: boolean;
}

const FormButtons = (props: IFormButtonsPros): JSX.Element => {
  const {
    editMode,
    isEditable,
    isCommercial,
    profilePageEnhancementsEnabled,
    onEditClicked,
    onSubmitClicked,
    onCancelClicked,
    form,
    isValidatingAddress,
    isProfileUpdateRequired
  } = props;

  const dirtyFields = Object.keys(form.getState().dirtyFields);
  const formState = form.getState();

  // registrationIdentifiers is a display-only field that is removed from the screen when a user clicks edit.
  // This causes it to be in the dirtyFields every time.
  // We want to ignore the change in that field
  // So, if that is the only change detected, we disable submit.
  const editsMade = formState.dirty && !(dirtyFields.length === 1 && dirtyFields[0] === 'registrationIdentifiers');

  // Check if documents array has been modified from the initial state
  const documentsChanged = hasDocumentsChanged(formState.initialValues, formState.values);

  // Enable submit if there are edits OR if documents have changed
  const isSubmitEnabled = editsMade || documentsChanged;

  if (!editMode) {
    return (
      <button
        className="profile-edit-button btn btn-primary"
        type="button"
        disabled={!isEditable}
        onClick={onEditClicked}
        aria-label={localeService.getText('ProfileV2', 'Edit')}
      >
        {localeService.getText('ProfileV2', 'Edit')}
      </button>
    );
  }

  return (
    <div className="profile-submit-edit-buttons" aria-label="Profile form actions">
      <button
        id="profileFormSubmitBtnId"
        className="profile-submit-button btn btn-primary"
        type="button"
        onClick={onSubmitClicked}
        disabled={(!isSubmitEnabled && !isProfileUpdateRequired) || isValidatingAddress}
        aria-label={localeService.getText('ProfileV2', 'Submit')}
      >
        {localeService.getText('ProfileV2', 'Submit')}
      </button>
      {!isCommercial && !isProfileUpdateRequired && (
        <button
          className="profile-cancel-button btn btn-outline-primary"
          type="button"
          onClick={onCancelClicked}
          aria-label={localeService.getText('ProfileV2', 'Cancel')}
        >
          {localeService.getText('ProfileV2', 'Cancel')}
        </button>
      )}
    </div>
  );
};

const mapStateToProps = ({
  profile,
  nonprofit,
  agentVerification,
  meoLocale,
  flags
}: IApplicationState): IPropsFromState => ({
  editMode: profile.editMode,
  showConfirmation: profile.showConfirmation,
  showAddressPopup: profile.showAddressPopup,
  isCommercial: profile.isCommercial,
  profileState: profile,
  profileError: profile.error,
  emailVerificationRequired: isEmailVerificationRequired(nonprofit, agentVerification),
  meoLocale: meoLocale,
  featureFlags: flags
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setEditMode: (editModeOn: boolean) => dispatch(setEditMode(editModeOn)),
  setShowConfirmation: (showConfirmation: boolean) => dispatch(setShowConfirmation(showConfirmation)),
  setShowAddressPopup: (showAddressPopup: boolean) => dispatch(setShowAddressPopup(showAddressPopup)),
  setCommercial: (setCommercial: (arg0: any) => any) => dispatch(setCommercial(setCommercial)),
  saveProfile: (profile: IProfile) => dispatch(saveProfile(profile)),
  createProfile: (profile: IProfile) => dispatch(createProfile(profile))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withAITracking(telemetryService.aiReactPlugin, ProfilePageV2));

